import styles from './Item.module.css';

import imageCompression from 'browser-image-compression';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';

import Cancel from '@mui/icons-material/Cancel';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Save from '@mui/icons-material/Save';

import { styled } from '@mui/material/styles';

import { useState, useEffect, useRef, useCallback, memo } from 'react';

// MARK: Edit Item
export const ItemEdit = memo(({ item, onChange, onSaveItem, onCancel }) => {
    const [ error, setError ] = useState(null);
    const [ requiredFields, setRequiredFields ] = useState([]);
    const [ preview, setPreview ] = useState(null);
    const [ photo, setPhoto ] = useState(null);
    const [ photoChanged, setPhotoChanged ] = useState(false);
    const [ imageError, setImageError ] = useState(false);
    const [ donationWarning, setDonationWarning ] = useState(false);

    const originalDonation = item.donation || false;

    const photoFileRef = useRef(null);

    const VisuallyHiddenInput = styled('input')({
        clip: 'rect(0 0 0 0)',
        clipPath: 'inset(50%)',
        height: 1,
        overflow: 'hidden',
        position: 'absolute',
        bottom: 0,
        left: 0,
        whiteSpace: 'nowrap',
        width: 1,
    });

    // MARK: input change
    const handleOnChange = useCallback((e) => {
        const { name, value, type, checked, required } = e.target;
        const fieldValue = type === 'checkbox' ? checked : value;

        onChange(e);

        if (name === 'donation' && !checked && originalDonation) {
            // Donation was deselected from a previously saved donation
            setDonationWarning(true);
        }

        if (name === 'category') {
            if (!fieldValue || fieldValue.trim() === '') {
                setRequiredFields(prev => prev.includes(name) ? prev : [ ...prev, name ]);
            } else {
                setRequiredFields(prev => prev.filter(field => field !== name));
            }
        } else if (name === 'price' || name === 'quantity') {
            if (!fieldValue || isNaN(fieldValue) || parseFloat(fieldValue) <= 0) {
                setRequiredFields(prev => prev.includes(name) ? prev : [ ...prev, name ]);
            } else {
                setRequiredFields(prev => prev.filter(field => field !== name));
            }
        } else if (required) {
            if (!fieldValue || fieldValue.trim() === '') {
                setRequiredFields(prev => prev.includes(name) ? prev : [ ...prev, name ]);
            } else {
                setRequiredFields(prev => prev.filter(field => field !== name));
            }
        }
    }, [ onChange, originalDonation ]);

    // MARK: file change
    const handleOnFileChange = useCallback(async (event) => {
        const file = event.target.files[ 0 ];

        if (!file || !file.type.match('image.*')) {
            setError('Please select a valid image file.');

            return;
        }

        setPhotoChanged(true);

        try {
            const options = {
                maxSizeMB: 0.2, // 200KB max
                maxWidthOrHeight: 600,
                useWebWorker: true,
                fileType: 'image/png'
            };

            const compressedFile = await imageCompression(file, options);
            const reader = new FileReader();

            reader.onloadend = () => {
                setPreview(reader.result);
                setImageError(false); // Reset error state for new image
            };

            reader.readAsDataURL(compressedFile);

            setPhoto(compressedFile);
        } catch (error) {
            setError('Failed to process image');

            console.error('Image compression error:', error);
        } finally {
            event.target.value = null; // Reset file input

            setPhotoChanged(false);
        }
    }, []);

    // MARK: handle cancel
    const handleCancel = useCallback(() => {
        onCancel();
    }, [ onCancel ]);

    // MARK: handle save
    const handleSaveItem = useCallback(() => {
        onSaveItem(item, photo);
    }, [ onSaveItem, item, photo ]);

    // MARK: useEffects
    useEffect(() => {
        setPreview(item?.imageUrl ? item.imageUrl : null);
    }, [ item?.imageUrl ]);

    useEffect(() => {
        const missing = [];

        if (!item.name) missing.push('name');
        if (!item.brand) missing.push('brand');
        if (!item.size) missing.push('size');
        if (!item.category) missing.push('category');
        if (!item.price || item.price <= 0) missing.push('price');
        if (!item.quantity || item.quantity < 1) missing.push('quantity');

        setRequiredFields(missing);
    }, [ item.brand, item.category, item.name, item.price, item.quantity, item.size ]);

    // MARK: render
    return (
        <div className={ styles.form }>
            { /* MARK: Item Information */ }
            <div className={ styles.formSection }>
                <h3>Item Information</h3>
                <div className={ styles.formRow }>
                    <div className={ styles.formField }>
                        <label htmlFor="itemName">Item Name</label>
                        <input
                            type="text"
                            id="name"
                            name="name"
                            autoComplete='item-name'
                            required
                            value={ item.name || '' }
                            onChange={ handleOnChange }
                            className={ requiredFields.includes('name') ? styles.required : '' }
                        />

                        { requiredFields.includes('name') && <Alert severity="error" variant='outlined'>Item name is required.</Alert> }
                    </div>

                    <div className={ styles.formField }>
                        <label htmlFor="itemBrand">Brand</label>
                        <input
                            type="text"
                            id="brand"
                            name="brand"
                            autoComplete='item-brand'
                            required
                            value={ item.brand || '' }
                            onChange={ handleOnChange }
                            className={ requiredFields.includes('brand') ? styles.required : '' }
                        />

                        { requiredFields.includes('brand') && <Alert severity="error" variant='outlined'>Brand is required.</Alert> }
                    </div>
                </div>

                <div className={ styles.formRow }>
                    <div className={ styles.formField }>
                        <label htmlFor="itemDesc">Description</label>
                        <input
                            type="text"
                            id="desc"
                            name="desc"
                            autoComplete='item-desc'
                            value={ item.desc || '' }
                            onChange={ handleOnChange }
                            className={ requiredFields.includes('desc') ? styles.required : '' }
                        />
                    </div>

                    <div className={ styles.formField }>
                        <label htmlFor="itemColor">Color</label>
                        <input
                            type="text"
                            id="color"
                            name="color"
                            autoComplete='item-color'
                            value={ item.color || '' }
                            onChange={ handleOnChange }
                            className={ requiredFields.includes('color') ? styles.required : '' }
                        />
                    </div>
                </div>

                <div className={ styles.formRow }>


                    <div className={ styles.formField }>
                        <label htmlFor="itemSize">Size</label>
                        <input
                            type="text"
                            id="size"
                            name="size"
                            autoComplete='item-size'
                            required
                            value={ item.size || '' }
                            onChange={ handleOnChange }
                            className={ requiredFields.includes('size') ? styles.required : '' }
                        />

                        { requiredFields.includes('size') && <Alert severity="error" variant='outlined'>Size is required.</Alert> }
                    </div>

                    <div className={ styles.formField }>
                        <label htmlFor="itemGender">Gender</label>
                        <input
                            type="text"
                            id="gender"
                            name="gender"
                            autoComplete='item-gender'
                            value={ item.gender || '' }
                            onChange={ handleOnChange }
                            className={ requiredFields.includes('gender') ? styles.required : '' }
                        />
                    </div>
                </div>

                <div className={ styles.formRow }>

                    <div className={ styles.formField }>
                        <label htmlFor="itemCategory">Category</label>
                        <Select
                            id="category"
                            name="category"
                            value={ item.category || '' }
                            onChange={ handleOnChange }
                            className={ requiredFields.includes('category') ? styles.required : '' }
                            MenuProps={ { disableScrollLock: true } }
                            required
                        >
                            <MenuItem value="1">Skis & Boards</MenuItem>
                            <MenuItem value="2">Poles & Tools</MenuItem>
                            <MenuItem value="3">Boots & Footwear</MenuItem>
                            <MenuItem value="4">Clothing & Outerwear</MenuItem>
                            <MenuItem value="5">Helmets & Goggles</MenuItem>
                            <MenuItem value="6">Apparel</MenuItem>
                            <MenuItem value="7">Accessories</MenuItem>
                            <MenuItem value="0">Other</MenuItem>
                        </Select>

                        { requiredFields.includes('category') && <Alert severity="error" variant='outlined'>Category is required.</Alert> }
                    </div>
                </div>

                { /* MARK: Photo Upload */ }
                <div className={ styles.formRow }>
                    <div className={ styles.formField }>
                        <label htmlFor="itemPhoto">Photo</label>
                        <Button
                            component="label"
                            variant="contained"
                            tabIndex={ -1 }
                            startIcon={ <CloudUploadIcon /> }
                            loading={ photoChanged }
                            loadingPosition="start"
                        >
                            Upload Image
                            <VisuallyHiddenInput
                                type="file"
                                onChange={ handleOnFileChange }
                                accept="image/*"
                            />
                        </Button>
                    </div>

                    <div className={ styles.formField }>
                        <label htmlFor='photoFile'>Preview</label>
                        <div className={ styles.photoContainer }>
                            { preview && !imageError ? (
                                <img src={ preview } alt="Preview" className={ styles.photo } onError={ () => setImageError(true) } />
                            ) : (
                                <Typography variant="body2" color="textSecondary" className={ styles.photoPlaceholder }>
                                    No Photo Selected
                                </Typography>
                            ) }
                        </div>

                        <input
                            type="file"
                            id="photoFile"
                            name="photoFile"
                            accept="image/*"
                            ref={ photoFileRef }
                            onChange={ handleOnFileChange }
                            style={ { display: 'none' } }
                        />
                    </div>
                </div>
            </div>

            { /* MARK: Item Pricing */ }
            <div className={ styles.formSection }>
                <h3>Pricing Information</h3>

                <div className={ styles.formRow }>
                    <div className={ styles.formField }>
                        <label htmlFor="itemPrice">Price</label>
                        <input
                            type="number"
                            id="price"
                            name="price"
                            autoComplete='item-price'
                            min={ 0 }
                            step="0.01"
                            required
                            value={ item.price ?? '' }
                            onChange={ handleOnChange }
                            className={ requiredFields.includes('price') ? styles.required : '' }
                        />

                        { requiredFields.includes('price') && <Alert severity="error" variant='outlined'>Price must be greater than 0.</Alert> }
                    </div>

                    <div className={ styles.formField }>
                        <label htmlFor="itemQuantity">Quantity</label>
                        <input
                            type="number"
                            id="quantity"
                            name="quantity"
                            autoComplete='item-quantity'
                            step="1"
                            required
                            value={ item.quantity ?? '' }
                            onChange={ handleOnChange }
                            className={ requiredFields.includes('quantity') ? styles.required : '' }
                        />

                        { requiredFields.includes('quantity') && <Alert severity="error" variant='outlined'>Quantity must be greater than 0.</Alert> }
                    </div>
                </div>

                <div className={ styles.formRow }>
                    <div className={ styles.formField }>
                        <label htmlFor="itemMinimumOffer">Minimum Offer <i><small>(not shown to buyers)</small></i></label>
                        <input
                            type="number"
                            id="minimum"
                            name="minimum"
                            autoComplete='item-minimum-offer'
                            min={ 0 }
                            step="0.01"
                            value={ item.minimum || '' }
                            onChange={ handleOnChange }
                            className={ requiredFields.includes('minimum') ? styles.required : '' }
                        />
                    </div>

                    <div className={ styles.formField }>
                        <label htmlFor="itemCondition">Condition</label>
                        <Select
                            id="condition"
                            name="condition"
                            value={ item.condition || '' }
                            onChange={ handleOnChange }
                            className={ requiredFields.includes('condition') ? styles.required : '' }
                            MenuProps={ { disableScrollLock: true } }
                        >
                            <MenuItem value="New">New</MenuItem>
                            <MenuItem value="Like New">Like New</MenuItem>
                            <MenuItem value="Very Good">Very Good</MenuItem>
                            <MenuItem value="Good">Good</MenuItem>
                            <MenuItem value="Acceptable">Acceptable</MenuItem>
                        </Select>
                    </div>
                </div>

                <Divider sx={ { my: 2 } } />

                <div className={ styles.formRow }>
                    <div className={ styles.formField } style={ { width: '100%' } }>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    name="donation"
                                    checked={ item.donation || false }
                                    onChange={ handleOnChange }
                                    color="primary"
                                />
                            }
                            label="I would like the proceeds from this item to support the Powder Ridge Ski Patrol."
                        />
                    </div>
                </div>
            </div>

            { /* MARK: Action Buttons */ }
            <div className={ styles.actions }>
                <Button
                    variant="outlined"
                    color="secondary"
                    onClick={ handleCancel }
                    startIcon={ <Cancel /> }
                >
                    Cancel
                </Button>

                <Button
                    variant="contained"
                    color="primary"
                    onClick={ handleSaveItem }
                    startIcon={ <Save /> }
                    disabled={ requiredFields.length > 0 }
                >
                    Save
                </Button>
            </div>

            { error && <Alert severity="error" variant='filled'>{ error }</Alert> }

            { donationWarning && (
                <Alert
                    severity="warning"
                    variant='filled'
                    onClose={ () => setDonationWarning(false) }
                >
                    You have deselected the donation option. A $2.00 listing fee is now due for this item.
                    You can pay now with a credit card or pay in person at drop-off.
                </Alert>
            ) }
        </div>
    );
});

ItemEdit.displayName = 'Edit Item';